import React, { useCallback } from "react";
import { AddressAutofill } from "@mapbox/search-js-react";
import { TextField, InputAdornment, Box, Grid, Stack } from "@mui/material";
import {
  Home as HomeIcon,
  LocationCity as CityIcon,
  Map as MapIcon,
  LocalPostOffice as PostalIcon,
} from "@mui/icons-material";
import { Controller, useFormContext, Control } from "react-hook-form";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY || "";

interface AddressFields {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
}

interface MapboxAddressInputProps {
  control: Control<any>;
  namePrefix?: string; // e.g., "address" for address.line1, address.city, etc.
  disabled?: boolean;
  mapboxCountry?: string;
}

export const MapboxAddressInput = ({
  control,
  mapboxCountry,
  namePrefix = "address",
  disabled = false,
}: MapboxAddressInputProps) => {
  const { setValue } = useFormContext();

  const handleRetrieve = useCallback(
    (res: any) => {
      console.log(res);
      const feature = res.features?.[0];
      if (!feature) return;

      const props = feature.properties;

      setValue(
        `${namePrefix}.line1`,
        props.address_line1 || props.full_address || "",
        {
          shouldValidate: true,
        },
      );
      setValue(`${namePrefix}.line2`, props.address_line2 || "", {
        shouldValidate: true,
      });
      setValue(`${namePrefix}.city`, props.place || props.locality || "", {
        shouldValidate: true,
      });
      setValue(`${namePrefix}.state`, props.region || "", {
        shouldValidate: true,
      });
      setValue(`${namePrefix}.postal_code`, props.postcode || "", {
        shouldValidate: true,
      });
    },
    [setValue, namePrefix],
  );

  return (
    <Stack spacing={2}>
      {/* Street Address with Autofill */}
      <Controller
        name={`${namePrefix}.line1`}
        control={control}
        rules={{ required: "Street address is required" }}
        render={({ field, fieldState: { error } }) => (
          <AddressAutofill
            accessToken={MAPBOX_TOKEN}
            onRetrieve={handleRetrieve}
            options={{
              country: mapboxCountry ?? "US,CA",
              language: "en",
            }}
          >
            <TextField
              {...field}
              label="Street Address"
              placeholder="Start typing your address..."
              required
              fullWidth
              disabled={disabled}
              error={!!error}
              helperText={error?.message}
              autoComplete="address-line1"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </AddressAutofill>
        )}
      />

      {/* Apt/Suite (optional) */}
      <Controller
        name={`${namePrefix}.line2`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Apt, Suite, Unit (optional)"
            placeholder="Apt 4B"
            fullWidth
            disabled={disabled}
            autoComplete="address-line2"
            InputLabelProps={{ shrink: true }}
          />
        )}
      />

      {/* City and State */}
      <Grid container spacing={0} alignItems="stretch">
        <Grid item xs={5} sx={{ pr: 2 }}>
          <Controller
            name={`${namePrefix}.city`}
            control={control}
            rules={{ required: "City is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="City"
                placeholder="Toronto"
                required
                fullWidth
                disabled={disabled}
                error={!!error}
                helperText={error?.message}
                autoComplete="address-level2"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CityIcon
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={7}>
          <Controller
            name={`${namePrefix}.state`}
            control={control}
            rules={{ required: "State/Province is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="State / Province"
                placeholder="Ontario"
                required
                fullWidth
                disabled={disabled}
                error={!!error}
                helperText={error?.message}
                autoComplete="address-level1"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MapIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      {/* Postal Code */}
      <Controller
        name={`${namePrefix}.postal_code`}
        control={control}
        rules={{ required: "Postal code is required" }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="Postal / ZIP Code"
            placeholder="M5V 1A1"
            required
            fullWidth
            disabled={disabled}
            error={!!error}
            helperText={error?.message}
            autoComplete="postal-code"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PostalIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Stack>
  );
};

export default MapboxAddressInput;
